/**
 * useFormContext Hook
 *
 * A wrapper around Formik's useFormikContext that provides a library-agnostic API.
 * Returns form state and actions for programmatic form control.
 *
 * @example
 * ```typescript
 * import { useFormContext } from '@/forms/core';
 *
 * const MyComponent = () => {
 *   const { values, setFieldValue, submitForm } = useFormContext();
 *
 *   const handleClick = () => {
 *     setFieldValue('field', 'newValue');
 *   };
 *
 *   return <button onClick={handleClick}>Update</button>;
 * };
 * ```
 */

import { useFormikContext } from 'formik';
import type { FormContextValue } from '../../types';

/**
 * Hook to access form context (state and actions)
 *
 * @returns Form state and action methods
 */
export function useFormContext<
  T = Record<string, unknown>,
>(): FormContextValue<T> {
  const formik = useFormikContext<T>();

  return {
    // State
    values: formik.values,
    errors: formik.errors as Record<string, string | undefined>,
    touched: formik.touched as Record<string, boolean>,
    dirty: formik.dirty,
    isValid: formik.isValid,
    isSubmitting: formik.isSubmitting,
    isValidating: formik.isValidating,
    submitCount: formik.submitCount,

    // Actions
    setFieldValue: formik.setFieldValue,
    setFieldTouched: formik.setFieldTouched,
    setFieldError: formik.setFieldError,
    setValues: formik.setValues,
    setErrors: formik.setErrors,
    setTouched: formik.setTouched,
    validateField: formik.validateField,
    validateForm: formik.validateForm as () => Promise<Record<string, string>>,
    submitForm: formik.submitForm,
    resetForm: formik.resetForm,
  };
}

/**
 * Hook to access only form values (optimized for render performance)
 *
 * @example
 * ```typescript
 * const values = useFormValues();
 * console.log(values.email);
 * ```
 */
export function useFormValues<T = Record<string, unknown>>(): T {
  const { values } = useFormikContext<T>();
  return values;
}

/**
 * Hook to access only form state (no actions)
 *
 * @example
 * ```typescript
 * const { dirty, isSubmitting, isValid } = useFormState();
 * ```
 */
export function useFormState<T = Record<string, unknown>>() {
  const formik = useFormikContext<T>();

  return {
    values: formik.values,
    errors: formik.errors as Record<string, string | undefined>,
    touched: formik.touched as Record<string, boolean>,
    dirty: formik.dirty,
    isValid: formik.isValid,
    isSubmitting: formik.isSubmitting,
    isValidating: formik.isValidating,
    submitCount: formik.submitCount,
  };
}

/**
 * Hook to access only form actions (no state)
 *
 * @example
 * ```typescript
 * const { setFieldValue, submitForm } = useFormActions();
 * ```
 */
export function useFormActions<T = Record<string, unknown>>() {
  const formik = useFormikContext<T>();

  return {
    setFieldValue: formik.setFieldValue,
    setFieldTouched: formik.setFieldTouched,
    setFieldError: formik.setFieldError,
    setValues: formik.setValues,
    setErrors: formik.setErrors,
    setTouched: formik.setTouched,
    validateField: formik.validateField,
    validateForm: formik.validateForm as () => Promise<Record<string, string>>,
    submitForm: formik.submitForm,
    resetForm: formik.resetForm,
  };
}
